/** Menu → Deals → Automatic discounts: the editor (F-DEAL-N3). */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Gift, Info, Layers, Wallet } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { cn } from '@/lib/utils'
import { toastHelpers } from '@/lib/toast-helpers'
import type { AutoDiscountAdmin } from '@/types/deals'
import {
  AUTO_DISCOUNTS_QUERY_KEY,
  DayToggles,
  Field,
  OrderTypeChips,
  ProductPicker,
  TargetPicker,
  TimeRangeFields,
  useDealsCatalog,
  useOrderTypeOptions,
} from './DealsControls'
import { describeAutoDiscountRule, errorText } from './dealsText'
import { draftFromRule, draftProblem, draftToInput, emptyDraft, type AutoDiscountDraft, type AutoKind } from './autoDiscountDraft'

const KINDS: Array<{ id: AutoKind; title: string; example: string; icon: ReactNode }> = [
  { id: 'buy_x_get_y', title: 'Buy X get Y', example: 'Buy 1 pizza, get 1 free', icon: <Gift className="h-4 w-4" /> },
  { id: 'multi_buy', title: 'Bundle price', example: '2 pizzas for Rs 1,800', icon: <Layers className="h-4 w-4" /> },
  { id: 'spend_over', title: 'Spend over', example: '10% off over Rs 2,000', icon: <Wallet className="h-4 w-4" /> },
]

function Choice({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'h-9 rounded-md border px-3 text-sm font-medium transition-colors',
        on ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {children}
    </button>
  )
}

export function AutoDiscountEditorDialog({
  open,
  onOpenChange,
  rule,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = new */
  rule: AutoDiscountAdmin | null
}) {
  const { formatCurrency } = useCurrency()
  const qc = useQueryClient()
  const catalog = useDealsCatalog()
  const orderTypes = useOrderTypeOptions()
  const otOptions = orderTypes.options.filter((o) => o.id !== 'online')
  const [d, setD] = useState<AutoDiscountDraft>(() => (rule ? draftFromRule(rule) : emptyDraft()))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const openedFor = useRef<string | null>(null)
  useEffect(() => {
    if (!open) {
      openedFor.current = null
      return
    }
    const key = rule?.id ?? 'new'
    if (openedFor.current === key) return
    openedFor.current = key
    setD(rule ? draftFromRule(rule) : emptyDraft())
    setError(null)
  }, [open, rule])

  const set = (patch: Partial<AutoDiscountDraft>) => setD((prev) => ({ ...prev, ...patch }))
  const products = catalog.all // a deal can be on any item, deals included

  const input = draftToInput(d)
  const preview = describeAutoDiscountRule(
    {
      kind: input.kind,
      buy_qty: input.buy_qty ?? 0,
      get_qty: input.get_qty ?? 0,
      get_percent: input.get_percent ?? 0,
      bundle_qty: input.bundle_qty ?? 0,
      bundle_price: input.bundle_price ?? 0,
      min_subtotal: input.min_subtotal ?? 0,
      reward_type: input.reward_type ?? '',
      reward_value: input.reward_value ?? 0,
      reward_product_id: input.reward_product_id ?? '',
      max_discount: input.max_discount ?? 0,
      buy_product_ids: input.buy_product_ids,
      buy_category_ids: input.buy_category_ids,
      get_product_ids: input.get_product_ids,
      get_category_ids: input.get_category_ids,
    },
    { product: catalog.productName, category: catalog.categoryName },
    formatCurrency,
  )

  const save = async () => {
    setError(null)
    const problem = draftProblem(d)
    if (problem) return setError(problem)
    setSaving(true)
    try {
      if (rule) await apiClient.updateAutoDiscount(rule.id, input)
      else await apiClient.createAutoDiscount(input)
      qc.invalidateQueries({ queryKey: AUTO_DISCOUNTS_QUERY_KEY })
      qc.invalidateQueries({ queryKey: ['menu-rules'] })
      toastHelpers.success(rule ? 'Discount saved' : 'Discount created', input.name)
      onOpenChange(false)
    } catch (e) {
      setError(errorText(e, 'Could not save the discount'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{rule ? `Edit ${rule.name}` : 'New automatic discount'}</DialogTitle>
          <DialogDescription>Applied by itself on the till and online when the bill qualifies — no code needed.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Type */}
          <div className="grid gap-2 sm:grid-cols-3">
            {KINDS.map((k) => (
              <button
                key={k.id}
                type="button"
                aria-pressed={d.kind === k.id}
                onClick={() => set({ kind: k.id })}
                className={cn(
                  'rounded-lg border p-3 text-left transition-colors',
                  d.kind === k.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/60',
                )}
              >
                <span className={cn('flex items-center gap-2 text-sm font-medium', d.kind === k.id && 'text-primary')}>
                  {k.icon}
                  {k.title}
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">{k.example}</span>
              </button>
            ))}
          </div>

          <Field label="Name" htmlFor="ad-name" hint="Shown on the receipt as “Deal · name”.">
            <Input id="ad-name" value={d.name} placeholder="Buy 1 Get 1 Pizza" onChange={(e) => set({ name: e.target.value })} />
          </Field>

          {/* Kind-specific */}
          {d.kind === 'buy_x_get_y' && (
            <section className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Field label="Buy" htmlFor="ad-buy">
                  <Input id="ad-buy" type="number" min={1} value={d.buy_qty} onChange={(e) => set({ buy_qty: e.target.value })} />
                </Field>
                <Field label="Get" htmlFor="ad-get">
                  <Input id="ad-get" type="number" min={1} value={d.get_qty} onChange={(e) => set({ get_qty: e.target.value })} />
                </Field>
                <Field label="Off the extra item (%)" htmlFor="ad-pct" hint="100% = free">
                  <Input id="ad-pct" type="number" min={1} max={100} value={d.get_percent} onChange={(e) => set({ get_percent: e.target.value })} />
                </Field>
              </div>
              <Field label="When they buy">
                <TargetPicker
                  products={products}
                  categories={catalog.categories}
                  productIds={d.buy_product_ids}
                  categoryIds={d.buy_category_ids}
                  onChange={(p, c) => set({ buy_product_ids: p, buy_category_ids: c })}
                  money={formatCurrency}
                />
              </Field>
              <Field label="The extra item is">
                <div className="flex flex-wrap gap-1.5">
                  <Choice on={d.get_mode === 'same'} onClick={() => set({ get_mode: 'same' })}>
                    One of the same items
                  </Choice>
                  <Choice on={d.get_mode === 'different'} onClick={() => set({ get_mode: 'different' })}>
                    Something else
                  </Choice>
                </div>
              </Field>
              {d.get_mode === 'different' && (
                <Field label="Extra item can be" hint="For example: buy 2 burgers, get a drink.">
                  <TargetPicker
                    products={products}
                    categories={catalog.categories}
                    productIds={d.get_product_ids}
                    categoryIds={d.get_category_ids}
                    onChange={(p, c) => set({ get_product_ids: p, get_category_ids: c })}
                    money={formatCurrency}
                  />
                </Field>
              )}
              <p className="text-xs text-muted-foreground">The cheapest qualifying item is the one discounted.</p>
            </section>
          )}

          {d.kind === 'multi_buy' && (
            <section className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Number of items" htmlFor="ad-bq">
                  <Input id="ad-bq" type="number" min={2} value={d.bundle_qty} onChange={(e) => set({ bundle_qty: e.target.value })} />
                </Field>
                <Field label="For" htmlFor="ad-bp">
                  <Input id="ad-bp" type="number" min={0} value={d.bundle_price} placeholder="0" onChange={(e) => set({ bundle_price: e.target.value })} />
                </Field>
              </div>
              <Field label="On these items">
                <TargetPicker
                  products={products}
                  categories={catalog.categories}
                  productIds={d.buy_product_ids}
                  categoryIds={d.buy_category_ids}
                  onChange={(p, c) => set({ buy_product_ids: p, buy_category_ids: c })}
                  money={formatCurrency}
                />
              </Field>
            </section>
          )}

          {d.kind === 'spend_over' && (
            <section className="space-y-4">
              <Field label="Minimum spend" htmlFor="ad-min">
                <Input id="ad-min" type="number" min={0} className="max-w-xs" value={d.min_subtotal} placeholder="2000" onChange={(e) => set({ min_subtotal: e.target.value })} />
              </Field>
              <Field label="Only count spend on (optional)" hint="Leave empty to count the whole bill.">
                <TargetPicker
                  products={products}
                  categories={catalog.categories}
                  productIds={d.buy_product_ids}
                  categoryIds={d.buy_category_ids}
                  onChange={(p, c) => set({ buy_product_ids: p, buy_category_ids: c })}
                  money={formatCurrency}
                />
              </Field>
              <Field label="The guest gets">
                <div className="flex flex-wrap gap-1.5">
                  <Choice on={d.reward_type === 'percent'} onClick={() => set({ reward_type: 'percent' })}>
                    % off
                  </Choice>
                  <Choice on={d.reward_type === 'amount'} onClick={() => set({ reward_type: 'amount' })}>
                    Amount off
                  </Choice>
                  <Choice on={d.reward_type === 'free_item'} onClick={() => set({ reward_type: 'free_item' })}>
                    A free item
                  </Choice>
                </div>
              </Field>
              {d.reward_type === 'percent' && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Percent off" htmlFor="ad-rv">
                    <Input id="ad-rv" type="number" min={1} max={100} value={d.reward_value} placeholder="10" onChange={(e) => set({ reward_value: e.target.value })} />
                  </Field>
                  <Field label="Maximum discount (optional)" htmlFor="ad-max">
                    <Input id="ad-max" type="number" min={0} value={d.max_discount} placeholder="No limit" onChange={(e) => set({ max_discount: e.target.value })} />
                  </Field>
                </div>
              )}
              {d.reward_type === 'amount' && (
                <Field label="Amount off" htmlFor="ad-amt">
                  <Input id="ad-amt" type="number" min={1} className="max-w-xs" value={d.reward_value} placeholder="200" onChange={(e) => set({ reward_value: e.target.value })} />
                </Field>
              )}
              {d.reward_type === 'free_item' && (
                <Field label="Free item" hint="The guest adds it to the bill; the discount takes it off.">
                  <div className="flex max-w-md">
                    <ProductPicker
                      products={products}
                      value={d.reward_product_id}
                      onChange={(id) => set({ reward_product_id: id })}
                      money={formatCurrency}
                    />
                  </div>
                </Field>
              )}
            </section>
          )}

          {/* When */}
          <section className="space-y-4 border-t pt-5">
            <h3 className="text-base font-semibold">When it runs</h3>
            <Field label="Days">
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  <Choice on={d.days.length === 0} onClick={() => set({ days: [] })}>
                    Every day
                  </Choice>
                  <Choice on={d.days.length > 0} onClick={() => d.days.length === 0 && set({ days: [5, 6, 0] })}>
                    Some days
                  </Choice>
                </div>
                {d.days.length > 0 && <DayToggles value={d.days} onChange={(days) => set({ days })} />}
              </div>
            </Field>
            <Field label="Hours">
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  <Choice on={!d.timed} onClick={() => set({ timed: false })}>
                    All day
                  </Choice>
                  <Choice on={d.timed} onClick={() => set({ timed: true })}>
                    Between
                  </Choice>
                </div>
                {d.timed && (
                  <TimeRangeFields
                    idPrefix="ad-time"
                    start={d.start_minute}
                    end={d.end_minute}
                    onChange={(s, e) => set({ start_minute: s, end_minute: e })}
                  />
                )}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts on (optional)" htmlFor="ad-from">
                <Input id="ad-from" type="date" value={d.valid_from} onChange={(e) => set({ valid_from: e.target.value })} />
              </Field>
              <Field label="Ends on (optional)" htmlFor="ad-to">
                <Input id="ad-to" type="date" value={d.valid_to} onChange={(e) => set({ valid_to: e.target.value })} />
              </Field>
            </div>
            <Field label="Where">
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <Switch id="ad-till" checked={d.till} onCheckedChange={(v) => set({ till: v })} />
                  <Label htmlFor="ad-till" className="text-sm font-normal">
                    Till &amp; Server tablet
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch id="ad-online" checked={d.online} onCheckedChange={(v) => set({ online: v })} />
                  <Label htmlFor="ad-online" className="text-sm font-normal">
                    Online store
                  </Label>
                </div>
              </div>
            </Field>
            <Field label="Order types">
              <OrderTypeChips options={otOptions} value={d.order_types} onChange={(order_types) => set({ order_types })} />
            </Field>
            <div className="flex items-center gap-2">
              <Switch id="ad-active" checked={d.is_active} onCheckedChange={(v) => set({ is_active: v })} />
              <Label htmlFor="ad-active" className="text-sm font-normal">
                Active
              </Label>
            </div>
          </section>

          <div className="rounded-lg border bg-muted/30 p-4 text-sm">
            <p className="font-medium">{preview}</p>
            <p className="mt-2 flex items-start gap-2 text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Only the single best discount applies to a bill. A promo code or manual discount replaces this deal only if it saves more.
            </p>
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : rule ? 'Save discount' : 'Create discount'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
